import { ZOOM_MAX, ZOOM_MIN, zoomLabel } from '../../lib/tiles';
import { useSettings } from '../../store/settings';
import { Choice, Section, Toggle } from './controls';

function ZoomSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className="muted">
          {zoomLabel(value)} ({value})
        </span>
      </div>
      <input
        type="range"
        min={ZOOM_MIN}
        max={ZOOM_MAX}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--accent)]"
      />
    </label>
  );
}

/** Where and when the photo on screen was taken, with a map (Settings → Media). */
export function PhotoInfoSection() {
  const info = useSettings((s) => s.photoInfo);
  const update = useSettings((s) => s.update);
  return (
    <Section
      title="Photo location & date"
      description="Where and when the photo on screen was taken, with a map. It appears for photos that carry this information: OneDrive photos and photos added from this device."
    >
      <Toggle label="Show photo location & date" checked={info.enabled} onChange={(enabled) => update('photoInfo', { enabled })} />
      {info.enabled && (
        <>
          <Toggle label="Town or city" checked={info.showCity} onChange={(showCity) => update('photoInfo', { showCity })} />
          <Toggle
            label="County or district"
            checked={info.showDistrict}
            onChange={(showDistrict) => update('photoInfo', { showDistrict })}
          />
          <Toggle label="Country" checked={info.showCountry} onChange={(showCountry) => update('photoInfo', { showCountry })} />
          <Toggle label="Date taken" checked={info.showDate} onChange={(showDate) => update('photoInfo', { showDate })} />
          <Toggle label="Time taken" checked={info.showTime} onChange={(showTime) => update('photoInfo', { showTime })} />
          <Toggle label="Map" checked={info.showMap} onChange={(showMap) => update('photoInfo', { showMap })} />
          {info.showMap && (
            <>
              <Choice
                options={[
                  { id: 'fixed', name: 'Fixed zoom', description: 'The map stays at the zoom you choose' },
                  { id: 'gradual', name: 'Zoom in slowly', description: 'From a wide view in to your zoom while the photo is shown' },
                ]}
                value={info.zoomMode}
                onChange={(zoomMode) => update('photoInfo', { zoomMode })}
              />
              {info.zoomMode === 'gradual' && (
                <ZoomSlider
                  label="Start from"
                  value={info.zoomFrom}
                  onChange={(zoomFrom) => update('photoInfo', { zoomFrom, zoom: Math.max(info.zoom, zoomFrom) })}
                />
              )}
              <ZoomSlider
                label={info.zoomMode === 'gradual' ? 'Zoom in to' : 'Zoom'}
                value={info.zoom}
                onChange={(zoom) =>
                  update('photoInfo', info.zoomMode === 'gradual' ? { zoom, zoomFrom: Math.min(info.zoomFrom, zoom) } : { zoom })
                }
              />
            </>
          )}
        </>
      )}
    </Section>
  );
}
